import React from 'react';
import { motion } from 'framer-motion';
import { Mail, MapPin, Linkedin, Twitter, Youtube, Send } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_miami-soft-dev/artifacts/f5hjp5ig_favicon.svg";
const CALENDLY_URL = "https://calendly.com/yaenlineasoft/30min";

const socialLinks = [
  { id: 'linkedin', icon: Linkedin, href: '#', label: 'LinkedIn' },
  { id: 'twitter', icon: Twitter, href: '#', label: 'Twitter/X' },
  { id: 'youtube', icon: Youtube, href: '#', label: 'YouTube' },
  { id: 'telegram', icon: Send, href: '#', label: 'Telegram' },
];

const quickLinks = [
  { label: 'Services', href: '#services' },
  { label: 'Why Us', href: '#why-us' },
  { label: 'Process', href: '#process' },
  { label: 'Testimonials', href: '#testimonials' },
  { label: 'FAQ', href: '#faq' },
];

const services = [
  'Custom Software Development',
  'Web Development',
  'Mobile App Development',
  'Business Automation',
  'Systems & Integrations',
];

const Footer = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer
      className="relative pt-20 pb-8 overflow-hidden border-t border-white/10"
      data-testid="footer"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-[#030305]" />
      
      {/* Gradient accent */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full h-32 bg-gradient-to-t from-violet-600/10 to-transparent" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <a href="/" className="flex items-center gap-3 mb-6 group" data-testid="footer-logo">
              <img 
                src={LOGO_URL} 
                alt="Yaenlinea Soft LLC Logo" 
                className="h-10 w-10 transition-transform group-hover:scale-110"
              />
              <span className="font-semibold text-xl text-white">Yaenlinea Soft</span>
            </a>
            <p className="text-gray-500 text-sm leading-relaxed mb-6">
              Custom software development company in Miami, FL. We build web apps, 
              mobile apps, and AI-powered solutions for businesses worldwide.
            </p>
            <div className="space-y-3">
              <a 
                href="mailto:support@yaenlineasoft.com" 
                className="flex items-center gap-3 text-gray-400 hover:text-violet-400 transition-colors text-sm"
                data-testid="footer-email"
              >
                <Mail className="w-4 h-4" />
                support@yaenlineasoft.com
              </a>
              <div className="flex items-center gap-3 text-gray-400 text-sm">
                <MapPin className="w-4 h-4" />
                Miami, Florida, USA
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className="text-gray-500 hover:text-violet-400 transition-colors text-sm"
                    data-testid={`footer-link-${link.label.toLowerCase().replace(' ', '-')}`}
                  >
                    {link.label}
                  </button>
                </li>
              ))}
              <li>
                <a
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-violet-400 hover:text-violet-300 transition-colors text-sm font-medium"
                  data-testid="footer-book-call"
                >
                  Book a Call
                </a>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-semibold mb-6">Services</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service}>
                  <span className="text-gray-500 text-sm">{service}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Social Links */}
          <div>
            <h4 className="text-white font-semibold mb-6">Connect With Us</h4>
            <p className="text-gray-500 text-sm mb-6">
              Follow us on social media for updates, tips, and insights on software development.
            </p>
            <div className="flex gap-4">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.id}
                    href={social.href}
                    aria-label={social.label}
                    className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-violet-400 hover:border-violet-500/50 transition-all"
                    data-testid={`footer-social-${social.id}`}
                  >
                    <Icon className="w-5 h-5" />
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-600 text-sm" data-testid="footer-copyright">
              © 2025 Yaenlinea Soft LLC. All rights reserved.
            </p>
            <div className="flex gap-6">
              <a href="#" className="text-gray-600 hover:text-gray-400 text-sm transition-colors">
                Privacy Policy
              </a>
              <a href="#" className="text-gray-600 hover:text-gray-400 text-sm transition-colors">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
